import { useState, useEffect } from "react";
import axios from "axios";
import {
  Container,
  TextField,
  Button,
  Box,
  Typography,
  Paper,
  Chip,
  InputAdornment,
  Snackbar,
  Alert,
} from "@mui/material";
import { Search, FilterList, FileDownload } from "@mui/icons-material";
import { MaterialReactTable } from "material-react-table";
import * as XLSX from "xlsx";
import { getToken, base_url } from "../../config/config";

const Invoices = () => {
  const [workData, setWorkData] = useState([]);
  const [editIndex, setEditIndex] = useState(null);
  const [editedRow, setEditedRow] = useState({});
  const [invoiceNo, setInvoiceNo] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredData, setFilteredData] = useState([]);

  // Toast state
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success", // 'success', 'error', 'warning', 'info'
  });

  // Toast handlers
  const showToast = (message, severity = "success") => {
    setToast({
      open: true,
      message,
      severity,
    });
  };

  const hideToast = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setToast((prev) => ({ ...prev, open: false }));
  };
  // Fetch all data from API
  const fetchData = async () => {
    try {
      const response = await axios.get(`${base_url}/v1/getBulkreport`);
      if (response.data.status === 1) {
        setWorkData(response.data.data);
      } else {
        console.error("Failed to fetch data:", response.data.message);
        showToast(
          `Fehler beim Laden der Daten: ${response.data.message}`,
          "error"
        );
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      showToast(
        "Fehler beim Laden der Daten. Bitte versuchen Sie es später erneut.",
        "error"
      );
    }
  };
  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredData(workData);
    } else {
      const filtered = workData.filter((item) => {
        const anrMatch =
          item.ANR &&
          item.ANR.toLowerCase().includes(searchQuery.toLowerCase());
        const bezeichnungMatch =
          item.Bezeichnung &&
          item.Bezeichnung.toLowerCase().includes(searchQuery.toLowerCase());
        const invoiceMatch =
          item.invoiceNo &&
          item.invoiceNo.toLowerCase().includes(searchQuery.toLowerCase());

        return anrMatch || bezeichnungMatch || invoiceMatch;
      });
      setFilteredData(filtered);
    }
  }, [searchQuery, workData]);
  const handleFileUpload = (event) => {
    const file = event.target.files[0];

    if (!file) {
      showToast("Keine Datei ausgewählt.", "warning");
      return;
    }

    const validTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];

    if (!validTypes.includes(file.type)) {
      showToast(
        "Bitte wählen Sie eine gültige Excel-Datei (.xlsx oder .xls).",
        "error"
      );
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const binaryStr = e.target.result;
        const workbook = XLSX.read(binaryStr, { type: "binary" });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

        const extractedData = jsonData
          .filter(
            (row) => row.Bezeichnung && row.Einzelpreis && row.Gesamtpreis
          )
          .map((row) => {
            // Extract only the first line (before any newline or line break)
            const firstLine = row.Bezeichnung.split(/[\r\n]+/)[0];
            let cleanedBezeichnung = firstLine.replace(/[^\d-]/g, "");

            if (cleanedBezeichnung.includes("-")) {
              const parts = cleanedBezeichnung.split("-");
              const lastPart = parts.pop();
              const truncatedLastPart = lastPart.slice(0, 2);
              cleanedBezeichnung = [...parts, truncatedLastPart].join("-");
            }

            return {
              ANR: row.ANR,
              Bezeichnung: cleanedBezeichnung,
              Einzelpreis: parseFloat(row.Einzelpreis),
              Gesamtpreis: parseFloat(row.Gesamtpreis),
              AdditionalText: row.AdditionalText || "",
              invoiceNo: invoiceNo,
            };
          });

        if (extractedData.length === 0) {
          showToast(
            "Keine gültigen Daten in der Excel-Datei gefunden. Stellen Sie sicher, dass die Spalten 'Bezeichnung', 'Einzelpreis' und 'Gesamtpreis' vorhanden sind.",
            "warning"
          );
          return;
        }

        console.log(extractedData);
        setWorkData(extractedData);
        showToast(
          `Excel-Datei erfolgreich importiert! ${extractedData.length} Einträge geladen.`,
          "success"
        );
      } catch (error) {
        console.error("Error processing file:", error);
        showToast(
          "Fehler beim Verarbeiten der Excel-Datei. Bitte überprüfen Sie das Dateiformat.",
          "error"
        );
      }
    };

    reader.onerror = () => {
      showToast("Fehler beim Lesen der Datei.", "error");
    };

    reader.readAsBinaryString(file);
  };
  const saveProcessedData = async () => {
    try {
      if (!workData.length) {
        showToast("Keine Daten zum Speichern vorhanden.", "warning");
        return;
      }

      if (!invoiceNo.trim()) {
        showToast("Bitte geben Sie eine Rechnungsnummer ein.", "warning");
        return;
      }

      console.log("workData", workData);
      const response = await axios.post(`${base_url}/v1/saveExcelData`, {
        invoiceNo,
        mergedData: workData,
      });

      if (response.data.status === 1) {
        console.log("Data successfully saved to the database.");
        showToast("Daten erfolgreich gespeichert!", "success");
        fetchData();
      } else {
        console.error("Failed to save data:", response.data.message);
        showToast(`Fehler beim Speichern: ${response.data.message}`, "error");
      }
    } catch (error) {
      console.error("Error saving processed data:", error);
      showToast(
        "Fehler beim Speichern der Daten. Bitte versuchen Sie es später erneut.",
        "error"
      );
    }
  };
  const handleEditClick = (index) => {
    setEditIndex(index);
    setEditedRow(filteredData[index]);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedRow((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const handleSaveClick = (index) => {
    const itemToUpdate = filteredData[index];
    const actualIndex = workData.findIndex(
      (item) =>
        item.ANR === itemToUpdate.ANR &&
        item.Bezeichnung === itemToUpdate.Bezeichnung &&
        item.invoiceNo === itemToUpdate.invoiceNo
    );

    if (actualIndex !== -1) {
      const updatedData = [...workData];
      updatedData[actualIndex] = { ...editedRow };
      setWorkData(updatedData);
      showToast("Eintrag erfolgreich aktualisiert!", "success");
    }
    setEditIndex(null);
  };

  const handleCancelClick = () => {
    setEditIndex(null);
  };
  const columns = [
    {
      header: "Bezeichnung (ANR)",
      accessorKey: "Bezeichnung",
      Cell: ({ cell, row }) =>
        editIndex === row.index ? (
          <TextField
            name="Bezeichnung"
            value={editedRow.Bezeichnung}
            onChange={handleInputChange}
            variant="outlined"
            size="small"
          />
        ) : (
          <Box>
            <div>{cell.getValue()}</div>
            {row.original.ANR && (
              <Typography color="primary" variant="body2">
                ANR: {row.original.ANR}
              </Typography>
            )}
          </Box>
        ),
    },
    {
      header: "Einzelpreis (€)",
      accessorKey: "Einzelpreis",
      Cell: ({ cell, row }) =>
        editIndex === row.index ? (
          <TextField
            name="Einzelpreis"
            type="number"
            value={editedRow.Einzelpreis}
            onChange={handleInputChange}
            variant="outlined"
            size="small"
          />
        ) : !isNaN(cell.getValue()) ? (
          parseFloat(cell.getValue()).toFixed(2)
        ) : (
          cell.getValue()
        ),
    },
    {
      header: "Gesamtpreis (€)",
      accessorKey: "Gesamtpreis",
      Cell: ({ cell, row }) =>
        editIndex === row.index ? (
          <TextField
            name="Gesamtpreis"
            type="number"
            value={editedRow.Gesamtpreis}
            onChange={handleInputChange}
            variant="outlined"
            size="small"
          />
        ) : !isNaN(cell.getValue()) ? (
          parseFloat(cell.getValue()).toFixed(2)
        ) : (
          cell.getValue()
        ),
    },
    {
      header: "Additional Text",
      accessorKey: "AdditionalText",
      Cell: ({ cell, row }) =>
        editIndex === row.index ? (
          <TextField
            name="AdditionalText"
            value={editedRow.AdditionalText}
            onChange={handleInputChange}
            variant="outlined"
            size="small"
          />
        ) : (
          cell.getValue()
        ),
    },
    {
      header: "Invoice No",
      accessorKey: "invoiceNo",
    },
    {
      header: "Actions",
      Cell: ({ row }) =>
        editIndex === row.index ? (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              onClick={() => handleSaveClick(row.index)}
              color="primary"
              size="small"
            >
              Save
            </Button>
            <Button onClick={handleCancelClick} color="secondary" size="small">
              Cancel
            </Button>
          </Box>
        ) : (
          <Button
            onClick={() => handleEditClick(row.index)}
            color="primary"
            size="small"
          >
            Edit
          </Button>
        ),
    },
  ];
  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: "bold", color: "#333" }}>
          Rechnungsverwaltung
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<FileDownload />}
            component="label"
            sx={{
              borderColor: "#1a1a1a",
              color: "#1a1a1a",
              "&:hover": { borderColor: "#333", backgroundColor: "#f5f5f5" },
            }}
          >
            EXCEL IMPORTIEREN
            <input
              type="file"
              accept=".xlsx, .xls"
              onChange={handleFileUpload}
              hidden
            />
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <TextField
            placeholder="Suche nach ANR, Bezeichnung oder Rechnungsnummer..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            sx={{ minWidth: "auto" }}
          >
            FILTER & SORTIERUNG
          </Button>
          {searchQuery && (
            <Chip
              label={`${filteredData.length} von ${workData.length} Einträgen`}
              color="primary"
              size="small"
            />
          )}
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Neue Rechnung hinzufügen
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <TextField
            label="Rechnung Nummer"
            type="text"
            value={invoiceNo}
            onChange={(e) => setInvoiceNo(e.target.value)}
            variant="outlined"
            size="small"
            sx={{ minWidth: 200 }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={saveProcessedData}
            disabled={!workData.length}
          >
            Verarbeitete Daten speichern
          </Button>
        </Box>
      </Paper>

      <Paper>
        {" "}
        <MaterialReactTable
          columns={columns}
          data={filteredData}
          enableGlobalFilter={false}
          enableColumnFilters={true}
          enablePagination={true}
          enableSorting={true}
          initialState={{
            pagination: { pageSize: 10 },
            density: "compact",
          }}
          muiTableProps={{
            sx: {
              "& .MuiTableHead-root": {
                backgroundColor: "#f5f5f5",
              },
            },
          }}
        />
      </Paper>

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={hideToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={hideToast}
          severity={toast.severity}
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Invoices;
